import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { api, setActiveOrganisationId } from "../lib/api";

const ApplicationCtx = createContext(null);

export function ApplicationProvider({ children }) {
  const { user, refreshMe } = useAuth();
  const [organisations, setOrganisations] = useState([]);
  const [sites, setSites] = useState([]);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextRevision, setContextRevision] = useState(0);
  const [effectiveWorkflow, setEffectiveWorkflow] = useState(null);
  const requestVersion = useRef(0);
  const workflowRequestVersion = useRef(0);
  const workflowScope = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    let version = ++requestVersion.current;
    setContextLoading(true);
    try {
      const [orgResult, siteResult] = await Promise.allSettled([api.get("/organisations"), api.get("/sites")]);
      if (version !== requestVersion.current) return;
      if (orgResult.status === "fulfilled") setOrganisations(orgResult.value.data || []);
      setSites(siteResult.status === "fulfilled" ? (siteResult.value.data || []) : []);
      setContextRevision((value) => value + 1);
    } finally {
      if (version === requestVersion.current) setContextLoading(false);
    }
  }, [user]);

  useEffect(() => { if (user) load(); else { setOrganisations([]); setSites([]); } }, [user, load]);
  useEffect(() => {
    const scopeKey = user ? `${user.organisation_id || ""}:${user.active_site_id || "all"}` : null;
    const version = ++workflowRequestVersion.current;
    if (!user) { workflowScope.current = null; setEffectiveWorkflow(null); return undefined; }
    // Preserve a successfully loaded model during refreshes of the *same*
    // scope. Clear immediately only on a genuine tenant/Site boundary.
    if (workflowScope.current !== scopeKey) setEffectiveWorkflow(null);
    const siteId = user.active_site_id;
    api.get("/workflow/effective", { params: siteId ? { site_id: siteId } : {} })
      .then(({data}) => {
        if (version !== workflowRequestVersion.current || !data?.stages?.length) return;
        workflowScope.current = scopeKey;
        setEffectiveWorkflow(data);
      })
      .catch(() => { /* a transient refresh failure must not erase valid navigation */ });
    return undefined;
  }, [user, contextRevision]);

  const switchOrganisation = useCallback(async (organisationId) => {
    let version = ++requestVersion.current;
    setSites([]); // Never display a previous tenant's hierarchy while switching.
    setContextLoading(true);
    try {
      await api.patch("/me/active-organisation", { organisation_id: organisationId });
      if (version !== requestVersion.current) return;
      setActiveOrganisationId(organisationId);
      const me = await refreshMe();
      const currentVersion = ++requestVersion.current;
      version = currentVersion;
      const [orgResult, siteResult] = await Promise.allSettled([api.get("/organisations"), api.get("/sites")]);
      if (currentVersion !== requestVersion.current) return;
      if (orgResult.status === "fulfilled") setOrganisations(orgResult.value.data || []);
      setSites(siteResult.status === "fulfilled" ? (siteResult.value.data || []) : []);
      setContextRevision((value) => value + 1);
      return me;
    } finally {
      if (version === requestVersion.current) setContextLoading(false);
    }
  }, [refreshMe]);

  const switchSite = useCallback(async (siteId) => {
    if (siteId && !sites.some((site) => site.id === siteId)) throw new Error("Site is outside effective scope");
    setContextLoading(true);
    try {
      await api.patch("/me/active-site", { site_id: siteId || null });
      await refreshMe();
      setContextRevision((value) => value + 1);
    } finally { setContextLoading(false); }
  }, [refreshMe, sites]);

  const activeOrganisationId = user?.organisation_id || null;
  const value = useMemo(() => ({
    organisations, activeOrganisationId,
    activeOrganisation: organisations.find((item) => item.id === activeOrganisationId) || user?.organisation || null,
    switchOrganisation, sites, activeSiteId: user?.active_site_id || null,
    activeSite: sites.find((item) => item.id === user?.active_site_id) || null,
    switchSite, permissionKeys: user?.permission_keys || [], lutionEntitlements: user?.lution_entitlements || [],
    effectiveSiteIds: user?.effective_site_ids || [], effectiveBlockIds: user?.effective_block_ids || [],
    contextLoading, contextRevision, effectiveWorkflow, refreshApplicationContext: load,
  }), [organisations, activeOrganisationId, user, switchOrganisation, sites, switchSite, contextLoading, contextRevision, effectiveWorkflow, load]);
  return <ApplicationCtx.Provider value={value}>{children}</ApplicationCtx.Provider>;
}

export const useApplication = () => useContext(ApplicationCtx);
